import React, { useState, useEffect } from "react";
import { Upload, Trash2, X, CheckCircle, Users, Eye, FileCheck, PenTool, ChevronDown, ChevronUp } from "lucide-react";
import {
  uploadEventRequiredDocument,
  getEventRequiredDocuments,
  deleteEventRequiredDocument,
  getDocumentAcknowledgments,
  updateDocumentSignatureFields,
  REQUIRED_DOCUMENT_TYPES,
  REQUIRED_DOCUMENT_TYPE_LABELS
} from "../../../services/EventDocumentService";
import DocumentSigningFieldEditor from "./DocumentSigningFieldEditor";
import DocumentSigningStatus from "./DocumentSigningStatus";
import { useAuth } from "../../../context/AuthContext";
import {
  createNotificationsForMany,
  NOTIFICATION_TYPES,
} from "../../../services/PebbleNotificationService";

const RequiredDocuments = ({ eventId, organizerId, approvedVendors = [] }) => {
  const { subscriptionTier } = useAuth();
  const isPro = subscriptionTier === 'pro' || subscriptionTier === 'premium' || subscriptionTier === 'enterprise';
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [viewingAcknowledgments, setViewingAcknowledgments] = useState(null);
  const [acknowledgments, setAcknowledgments] = useState([]);
  const [editingFieldsDoc, setEditingFieldsDoc] = useState(null);
  const [expandedSigningDoc, setExpandedSigningDoc] = useState(null);
  
  const [uploadForm, setUploadForm] = useState({
    file: null,
    title: '',
    type: REQUIRED_DOCUMENT_TYPES.OTHER,
    description: '',
    requiresSignature: false,
    requiresAcknowledgment: true,
    isRequired: true
  });

  useEffect(() => {
    if (eventId) {
      loadDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const result = await getEventRequiredDocuments(eventId);
      if (result.success) {
        setDocuments(result.documents);
      }
    } catch (err) {
      console.error('Error loading required documents:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm(prev => ({
        ...prev,
        file,
        title: prev.title || file.name.replace(/\.[^/.]+$/, "")
      }));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file) return;
    
    setUploading(true);
    setError(null);
    
    try {
      const result = await uploadEventRequiredDocument(eventId, organizerId, uploadForm.file, {
        title: uploadForm.title,
        type: uploadForm.type,
        description: uploadForm.description,
        requiresSignature: uploadForm.requiresSignature,
        requiresAcknowledgment: uploadForm.requiresAcknowledgment,
        isRequired: uploadForm.isRequired
      });
      
      if (result.success) {
        setIsModalOpen(false);
        setUploadForm({
          file: null,
          title: '',
          type: REQUIRED_DOCUMENT_TYPES.OTHER,
          description: '',
          requiresSignature: false,
          requiresAcknowledgment: true,
          isRequired: true
        });
        await loadDocuments();
      } else {
        setError(result.error || 'Failed to upload document');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this required document?')) return;
    
    try {
      const result = await deleteEventRequiredDocument(eventId, documentId);
      if (result.success) {
        await loadDocuments();
      }
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const handleSaveSignatureFields = async (fields) => {
    const result = await updateDocumentSignatureFields(eventId, editingFieldsDoc.id, fields, editingFieldsDoc.assignedVendorIds || [], editingFieldsDoc.signingDeadline || null);
    if (result.success) {
      setEditingFieldsDoc(null);
      await loadDocuments();
    }
  };

  const handleSaveAndSend = async (fields, vendorIds, deadline) => {
    const result = await updateDocumentSignatureFields(eventId, editingFieldsDoc.id, fields, vendorIds, deadline);
    if (result.success) {
      // Notify every assigned vendor
      if (vendorIds?.length > 0) {
        await createNotificationsForMany(vendorIds, {
          recipientType: 'mps',
          type: NOTIFICATION_TYPES.DOCUMENT_SIGNING_REQUIRED,
          title: 'Document ready for your signature',
          message: `"${editingFieldsDoc.title}" requires your signature. Open your event to review and sign.`,
          eventId,
          documentId: editingFieldsDoc.id,
          linkPath: `/mps/view-event-details/${eventId}?tab=documents`,
        });
      }
      setEditingFieldsDoc(null);
      await loadDocuments();
    }
  };

  const viewAcknowledgments = async (doc) => {
    setViewingAcknowledgments(doc);
    try {
      const result = await getDocumentAcknowledgments(eventId, doc.id);
      if (result.success) {
        setAcknowledgments(result.acknowledgments);
      }
    } catch (err) {
      console.error('Error loading acknowledgments:', err);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 transition-opacity duration-300">
        <p className="text-sm text-grey-500">Loading documents...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-grey-900">Required Documents</h2>
        <p className="text-sm text-grey-600 mt-1">
          Add documents that vendors must acknowledge or sign before their registration is complete. 
          Examples include venue regulations, liability waivers, and rules of conduct.
        </p>
      </div>

      {documents.length > 0 ? (
        <div className="space-y-3 mb-6">
          {documents.map((doc) => (
            <div 
              key={doc.id} 
              className="bg-white border border-grey-200 rounded-xl p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <FileCheck className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-grey-900">{doc.title}</h4>
                      {doc.isRequired && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                          Required
                        </span>
                      )}
                      {doc.requiresAcknowledgment === false && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-grey-100 text-grey-600 rounded-full">
                          Read Only
                        </span>
                      )}
                      {doc.requiresSignature && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                          Requires Signature
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-grey-500 mt-1">
                      {REQUIRED_DOCUMENT_TYPE_LABELS[doc.type] || doc.type}
                    </p>
                    {doc.description && (
                      <p className="text-sm text-grey-600 mt-2">{doc.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {doc.requiresSignature && (
                    isPro ? (
                      <>
                        <button
                          onClick={() => setEditingFieldsDoc(doc)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-purple-700 hover:text-purple-900 hover:bg-purple-50 rounded-lg transition"
                          title="Place signature fields"
                        >
                          <PenTool className="w-4 h-4" />
                          <span>{doc.signatureFields?.length > 0 ? "Edit Fields" : "Place Fields"}</span>
                        </button>
                        <button
                          onClick={() => setExpandedSigningDoc(expandedSigningDoc === doc.id ? null : doc.id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-grey-600 hover:text-grey-800 hover:bg-grey-100 rounded-lg transition"
                          title="View signing status"
                        >
                          <Users className="w-4 h-4" />
                          <span>Signatures</span>
                          {expandedSigningDoc === doc.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                      </>
                    ) : (
                      <span className="px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg cursor-default">
                        Pro · Document Signing
                      </span>
                    )
                  )}
                  {!doc.requiresSignature && (
                    <button
                      onClick={() => viewAcknowledgments(doc)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-grey-600 hover:text-grey-800 hover:bg-grey-100 rounded-lg transition"
                      title="View who acknowledged"
                    >
                      <Users className="w-4 h-4" />
                      <span>Acknowledgments</span>
                    </button>
                  )}
                  <a
                    href={doc.downloadURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-grey-500 hover:text-grey-700 hover:bg-grey-100 rounded-lg transition"
                    title="View Document"
                  >
                    <Eye className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => handleDelete(doc.id)}
                    className="p-2 text-grey-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="Delete Document"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Signing status expansion */}
              {doc.requiresSignature && expandedSigningDoc === doc.id && (
                <div className="mt-3 pt-3 border-t border-grey-100">
                  <DocumentSigningStatus
                    eventId={eventId}
                    document={doc}
                    assignedVendors={approvedVendors.filter(v =>
                      !doc.assignedVendorIds?.length || doc.assignedVendorIds.includes(v.id)
                    )}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-grey-50 border border-dashed border-grey-300 rounded-xl p-8 text-center mb-6">
          <FileCheck className="w-12 h-12 text-grey-400 mx-auto mb-3" />
          <h3 className="font-medium text-grey-700 mb-1">No required documents</h3>
          <p className="text-sm text-grey-500">Add documents that vendors must acknowledge when registering for your event.</p>
        </div>
      )}

      <button
        onClick={() => setIsModalOpen(true)}
        className="flex items-center gap-2 px-4 py-2 bg-pebble-red text-white rounded-lg hover:bg-red-700 transition font-medium"
      >
        <Upload className="w-4 h-4" />
        Add Required Document
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-grey-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-grey-900">Add Required Document</h3>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-1 hover:bg-grey-100 rounded-lg transition"
              >
                <X className="w-5 h-5 text-grey-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">Document File</label>
                <div className="border-2 border-dashed border-grey-300 rounded-lg p-4 text-center hover:border-grey-400 transition cursor-pointer">
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="req-doc-upload"
                    accept=".pdf,.doc,.docx"
                  />
                  <label htmlFor="req-doc-upload" className="cursor-pointer">
                    {uploadForm.file ? (
                      <div className="flex items-center justify-center gap-2 text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">{uploadForm.file.name}</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-grey-400 mx-auto mb-2" />
                        <p className="text-sm text-grey-600">Click to select a document</p>
                        <p className="text-xs text-grey-400 mt-1">PDF or DOC (max 10MB)</p>
                      </>
                    )}
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">Document Title</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-pebble-red focus:border-transparent"
                  placeholder="e.g., Venue Regulations 2025"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">Document Type</label>
                <select
                  value={uploadForm.type}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-pebble-red focus:border-transparent"
                >
                  {Object.entries(REQUIRED_DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">Description (Optional)</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-pebble-red focus:border-transparent"
                  rows={2}
                  placeholder="Brief description of what this document covers..."
                />
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={uploadForm.isRequired}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, isRequired: e.target.checked }))}
                    className="w-4 h-4 text-pebble-red border-grey-300 rounded focus:ring-pebble-red"
                  />
                  <div>
                    <span className="text-sm font-medium text-grey-700">Required for registration</span>
                    <p className="text-xs text-grey-500">Vendors must complete this document step to proceed with registration</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={uploadForm.requiresAcknowledgment}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, requiresAcknowledgment: e.target.checked }))}
                    className="w-4 h-4 text-pebble-red border-grey-300 rounded focus:ring-pebble-red"
                  />
                  <div>
                    <span className="text-sm font-medium text-grey-700">Requires acknowledgment</span>
                    <p className="text-xs text-grey-500">Vendors must click to acknowledge they have read this document</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={uploadForm.requiresSignature}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, requiresSignature: e.target.checked }))}
                    className="w-4 h-4 text-pebble-red border-grey-300 rounded focus:ring-pebble-red"
                  />
                  <div>
                    <span className="text-sm font-medium text-grey-700">Requires signature</span>
                    <p className="text-xs text-grey-500">
                      {isPro
                        ? "After uploading, place signature fields directly on the PDF — vendors draw their signature in-browser and a signed copy is saved."
                        : "Vendors type their full name as a legal signature. Upgrade to Pro to place signature fields on the PDF itself."}
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-grey-200 flex gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!uploadForm.file || !uploadForm.title || uploading}
                className="flex-1 px-4 py-2 bg-pebble-red text-white rounded-lg hover:bg-red-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Add Document'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingFieldsDoc && (
        <DocumentSigningFieldEditor
          document={editingFieldsDoc}
          existingFields={editingFieldsDoc.signatureFields || []}
          approvedVendors={approvedVendors}
          onSave={handleSaveSignatureFields}
          onSaveAndSend={handleSaveAndSend}
          onClose={() => setEditingFieldsDoc(null)}
        />
      )}

      {viewingAcknowledgments && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-grey-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-grey-900">Vendor Acknowledgments</h3>
                <p className="text-sm text-grey-500">{viewingAcknowledgments.title}</p>
              </div>
              <button 
                onClick={() => { setViewingAcknowledgments(null); setAcknowledgments([]); }}
                className="p-1 hover:bg-grey-100 rounded-lg transition"
              >
                <X className="w-5 h-5 text-grey-500" />
              </button>
            </div>
            
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {acknowledgments.length > 0 ? (
                <div className="space-y-3">
                  {acknowledgments.map((ack) => (
                    <div key={ack.vendorId} className="flex items-center justify-between p-3 bg-grey-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-grey-900">{ack.vendorName}</p>
                          <p className="text-xs text-grey-500">
                            Acknowledged: {formatDate(ack.acknowledgedAt)}
                          </p>
                        </div>
                      </div>
                      {ack.signature && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">Signed</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-grey-300 mx-auto mb-3" />
                  <p className="text-grey-500">No vendors have acknowledged this document yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequiredDocuments;
